import React, { useEffect, useState } from "react";
import { Image } from "semantic-ui-react";
import { Link, NavLink, useLocation } from "react-router-dom";

import { LogoutButton } from "../../components";
import { useAuth, usePermissions } from "../../hooks";
import styles from "./AdminLayout.module.scss";

const menuItems = [
  {
    label: "Resumen",
    path: "/admin",
    icon: "⌂",
    end: true,
    menuKey: "dashboard",
  },
  {
    label: "Productos",
    path: "/admin/products",
    icon: "▦",
    menuKey: "products",
  },
  {
    label: "Categorías",
    path: "/admin/categories",
    icon: "◈",
    menuKey: "categories",
  },
  {
    label: "Inventario",
    path: "/admin/inventory",
    icon: "▤",
    menuKey: "inventory",
  },
  {
    label: "Usuarios",
    path: "/admin/users",
    icon: "◉",
    menuKey: "users",
  },
  {
    label: "Roles",
    path: "/admin/roles",
    icon: "⛨",
    menuKey: "roles",
  },
  {
    label: "Configuración",
    path: "/admin/settings",
    icon: "⚙",
    menuKey: "settings",
  },
  {
    label: "Bitácora",
    path: "/admin/audit",
    icon: "☰",
    menuKey: "audit",
  },
];

const catalogItems = [
  {
    label: "Esquemas",
    path: "/admin/catalog/schema",
    icon: "◫",
    menuKey: "catalog_schema",
  },
  {
    label: "Clasificaciones",
    path: "/admin/catalog/classification",
    icon: "◇",
    menuKey: "catalog_classification",
  },
];

const pageInfo = {
  "/admin": {
    title: "Resumen",
    subtitle: "Visualiza el estado general de tu tienda.",
  },
  "/admin/products": {
    title: "Productos",
    subtitle: "Administra los productos de tu catálogo.",
  },
  "/admin/products/create": {
    title: "Nuevo producto",
    subtitle: "Agrega un nuevo producto a tu catálogo.",
  },
  "/admin/categories": {
    title: "Mantenimiento de Categorías",
    subtitle: "Organiza categorías, subcategorías y atributos.",
  },
  "/admin/inventory": {
    title: "Inventario",
    subtitle: "Controla existencias y movimientos de stock.",
  },
  "/admin/users": {
    title: "Usuarios",
    subtitle: "Administra los usuarios y sus roles de acceso.",
  },
  "/admin/users/create": {
    title: "Nuevo usuario",
    subtitle: "Crea una nueva cuenta de acceso al panel.",
  },
  "/admin/roles": {
    title: "Roles",
    subtitle: "Define roles y asigna permisos por menú y por acción.",
  },
  "/admin/roles/create": {
    title: "Nuevo rol",
    subtitle: "Crea un rol y define sus permisos.",
  },
  "/admin/settings": {
    title: "Configuración",
    subtitle: "Cambia la imagen del banner de Inicio.",
  },
  "/admin/catalog/schema": {
    title: "Mantenimiento de Esquemas",
    subtitle: "Configura los esquemas y estructuras del catálogo.",
  },
  "/admin/catalog/classification": {
    title: "Mantenimiento de Clasificaciones",
    subtitle: "Administra las clasificaciones del catálogo.",
  },
  "/admin/audit": {
    title: "Bitácora",
    subtitle: "Consulta quién hizo cada cambio en el sistema y cuándo.",
  },
};

function getUserInitial(user) {
  const userName = user?.name || user?.email || "A";

  return userName.trim().charAt(0).toUpperCase();
}

// Shopping bag (línea fina) en vez de la flecha genérica "↗": se
// distingue mejor como "ir a la tienda" y sigue la misma línea visual de
// los demás íconos del panel (trazo simple, sin relleno, hereda el color
// del botón/enlace que lo contiene vía currentColor).
function StoreIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <path d="M3 6h18" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

export function AdminLayout({ children }) {
  const location = useLocation();
  const { user } = useAuth();
  const { hasMenuAccess } = usePermissions();

  const visibleMenuItems = menuItems.filter((item) =>
    hasMenuAccess(item.menuKey)
  );
  const visibleCatalogItems = catalogItems.filter((item) =>
    hasMenuAccess(item.menuKey)
  );

  const isCatalogRoute = location.pathname.startsWith("/admin/catalog");

  const [isCatalogOpen, setIsCatalogOpen] = useState(isCatalogRoute);

  // En pantallas chicas el menú lateral se muestra como un cajón que se
  // desliza encima del contenido (ver AdminLayout.module.scss). Se cierra
  // solo al cambiar de página para que "Volver" al menú sea automático
  // después de elegir una opción.
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const currentPage = pageInfo[location.pathname] || {
    title: "Administración",
    subtitle: "Gestión de Lux & Glam.",
  };

  const userName = user?.name || "Administrador";
  const userRole = user?.role || "ADMIN";

  return (
    <div className={styles.AdminLayout}>
      <aside
        className={`${styles.Sidebar} ${
          isMobileMenuOpen ? styles.SidebarOpen : ""
        }`}
      >
        <button
          type="button"
          className={styles.SidebarCloseButton}
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Cerrar menú"
        >
          ✕
        </button>

        <div className={styles.Brand}>
          <Link to="/admin" className={styles.BrandLink}>
            <Image
              src="/images/logo-sidebar.png"
              alt="Lux & Glam"
              className={styles.Logo}
            />
          </Link>

          <span className={styles.AdminBadge} title={userName}>
            {userName}
          </span>
        </div>

        <nav className={styles.Navigation}>
          <p className={styles.NavigationTitle}>ADMINISTRACIÓN</p>

          {visibleMenuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `${styles.NavigationItem} ${
                  isActive ? styles.NavigationItemActive : ""
                }`
              }
            >
              <span className={styles.NavigationIcon}>{item.icon}</span>
              <span className={styles.NavigationLabel}>{item.label}</span>
            </NavLink>
          ))}

          {visibleCatalogItems.length > 0 && (
            <div className={styles.CatalogMenu}>
              <button
                type="button"
                className={`${styles.NavigationItem} ${
                  isCatalogRoute ? styles.NavigationItemActive : ""
                }`}
                onClick={() =>
                  setIsCatalogOpen((currentValue) => !currentValue)
                }
                aria-expanded={isCatalogOpen}
                aria-controls="catalog-submenu"
              >
                <span className={styles.NavigationIcon}>▣</span>
                <span className={styles.NavigationLabel}>Catálogo</span>
                <span
                  className={`${styles.CatalogArrow} ${
                    isCatalogOpen ? styles.CatalogArrowOpen : ""
                  }`}
                >
                  ▾
                </span>
              </button>

              {isCatalogOpen && (
                <div id="catalog-submenu" className={styles.CatalogSubmenu}>
                  {visibleCatalogItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        `${styles.CatalogSubmenuItem} ${
                          isActive ? styles.CatalogSubmenuItemActive : ""
                        }`
                      }
                    >
                      <span className={styles.CatalogSubmenuIcon}>
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className={styles.SidebarFooter}>
          <Link to="/" className={styles.StoreLink}>
            <StoreIcon /> Ver tienda
          </Link>
        </div>
      </aside>

      {isMobileMenuOpen && (
        <div
          className={styles.SidebarBackdrop}
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <section className={styles.MainContent}>
        <header className={styles.Header}>
          <div className={styles.HeaderLeft}>
            <button
              type="button"
              className={styles.MobileMenuButton}
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Abrir menú"
            >
              ☰
            </button>

            <div className={styles.HeaderPageInfo}>
              <p className={styles.Breadcrumb}>Administración</p>

              <h1 className={styles.PageTitle}>{currentPage.title}</h1>

              <p className={styles.PageSubtitle}>{currentPage.subtitle}</p>
            </div>
          </div>

          <div className={styles.HeaderActions}>
            {/*
             * Copia de "Ver tienda" solo para pantallas chicas: ahí el
             * enlace de la sidebar queda escondido dentro del cajón del
             * menú (ver AdminLayout.module.scss), así que sin esto no
             * había forma rápida de ir a la tienda desde el celular. En
             * escritorio se oculta porque ya está el de la sidebar.
             */}
            <Link
              to="/"
              className={styles.MobileStoreLink}
              aria-label="Ir a tienda"
              title="Ir a tienda"
            >
              <StoreIcon />
            </Link>

            <LogoutButton variant="icon" />

            <div className={styles.UserInfo}>
              <div className={styles.UserAvatar}>{getUserInitial(user)}</div>

              <div className={styles.UserText}>
                <p className={styles.UserName}>{userName}</p>
                <p className={styles.UserRole}>{userRole}</p>
              </div>
            </div>
          </div>
        </header>

        <main className={styles.Content}>{children}</main>
      </section>
    </div>
  );
}